import { useSelector } from "react-redux";

/**
 * A VQA only organization only requests VQAs, so its users never see a way to request, browse
 * or build VQQs.
 */

/** Sponsor rights that only ever apply to VQQs. */
export const VQQ_RIGHTS = [
  "Request RFIs",
  "View Requested RFIs",
  "Manage Requested & Released RFIs",
  "View Released RFIs",
  "RFI Template Builder",
  "Risk Categorization",
  "Download RFIs",
  "Compare RFI",
  "Request New RFIs",
];

/** @param {Record<string, unknown> | null | undefined} profile The `/Me` sponsor profile. */
export function isVqaOnlyOrg(profile) {
  return profile?.is_vqa_only === true || profile?.is_vqa_only === "true";
}

/**
 * Whether the org is VQA only, or null while the sponsor profile that says so is still loading.
 * A profile that never came is treated as an ordinary org's.
 * @param {Record<string, unknown> | null | undefined} profile
 * @param {boolean} profileLoading
 * @returns {boolean | null}
 */
export function vqaOnlyStatus(profile, profileLoading) {
  if (profile?.is_vqa_only !== undefined && profile?.is_vqa_only !== null) return isVqaOnlyOrg(profile);
  return profileLoading ? null : false;
}

/**
 * What a VQQ page does for the current user: a sponsor waits for the profile, so the page never
 * opens for a VQA only org, which is sent home instead.
 * @param {{ isSponsorUser: boolean, profile: Record<string, unknown> | null | undefined, profileLoading: boolean }} context
 * @returns {"wait" | "redirect" | "allow"}
 */
export function vqqRouteAccess({ isSponsorUser, profile, profileLoading }) {
  const status = isSponsorUser ? vqaOnlyStatus(profile, profileLoading) : false;
  if (status === null) return "wait";
  return status ? "redirect" : "allow";
}

/**
 * The rights that mean something for the org: a VQA only org keeps whatever VQQ rights were
 * granted to its users, but none of them take effect.
 * @param {string[] | null | undefined} rights
 * @param {boolean} isVqaOnly
 */
export function withoutVqqRights(rights, isVqaOnly) {
  const list = Array.isArray(rights) ? rights : [];
  return isVqaOnly ? list.filter((right) => !VQQ_RIGHTS.includes(right)) : list;
}

/**
 * Whether a library document can be requested: a VQA only org requests audits, never VQQs.
 * @param {string | null | undefined} documentType "RFI" for a VQQ, "VQA" for an audit.
 * @param {boolean} isVqaOnly
 */
export function canRequestLibraryDocument(documentType, isVqaOnly) {
  return !(isVqaOnly && String(documentType ?? "").toUpperCase() === "RFI");
}

const LIBRARY_DOCUMENT_TYPE_OPTIONS = [
  { docId: "RFI", label: "VQQ" },
  { docId: "VQA", label: "Audit Report" },
];
const VQA_DOCUMENTS_ONLY = ["VQA"];

/** The library's document type filter options: a VQA only org is offered audit reports only. */
export function libraryDocumentTypeOptions(isVqaOnly) {
  return LIBRARY_DOCUMENT_TYPE_OPTIONS
    .filter((option) => canRequestLibraryDocument(option.docId, isVqaOnly))
    .map((option) => ({ ...option, checked: false }));
}

/**
 * The document types a library search asks for: a VQA only org is never shown VQQs.
 * @param {string[]} selected The types picked in the filter; none means every type.
 * @param {boolean} isVqaOnly
 */
export function libraryDocumentTypes(selected, isVqaOnly) {
  return isVqaOnly ? VQA_DOCUMENTS_ONLY : selected;
}

/**
 * The HyperComply organization a sponsor's user joins. A VQA only sponsor has none and its
 * users need none; any other sponsor must have one before users can be added.
 * @param {boolean} isVqaOnly
 * @param {number | string | null | undefined} orgId
 * @returns {{ ok: boolean, hypercomplyOrgId: number | null }}
 */
export function resolveUserHyperComplyOrg(isVqaOnly, orgId) {
  if (isVqaOnly) return { ok: true, hypercomplyOrgId: null };
  const id = Number(orgId);
  return Number.isFinite(id) && id > 0
    ? { ok: true, hypercomplyOrgId: id }
    : { ok: false, hypercomplyOrgId: null };
}

export function useIsOrgVqaOnly() {
  const profile = useSelector((state) => state?.loggedInUser?.userDetails);
  return isVqaOnlyOrg(profile);
}
